import { useCallback, useEffect, useState } from 'react';
import { SearchFilterType, } from '@zurich/api/search';
import { useDebounce, useFetchApi } from '@zurich/common/hooks';
// create a not filter based on id's in results
function filterExcludeResultIds(results) {
    return results.reduce((acc, d) => {
        const found = acc.find((a) => a.indexId === d._indexId);
        //const value = { name: d.name, val: d.value };
        if (!found) {
            //acc.push(...value);
            acc.push({
                indexId: d._indexId,
                field: 'ids',
                filterType: SearchFilterType.NOT_IDS,
                values: [d._id],
                constraint: 'must',
            }); // not found, so need to add data property
        }
        else {
            //acc.push({ name: d.name, data: [{ value: d.value }, { count: d.count }] });
            found.values.push(d._id); // if found, that means data property exists, so just push new element to found.data.
        }
        return acc;
    }, []);
}
// TODO: 1. Needs documentation!
// TODO: 2. Debounce not working! Test: use global search, search for "Firma", then refine search and search for Postleitzahl -> only part of the Postleitzahl will be send
/**
 * General hook for generic search usecase
 * Provides things like loading more entries, fuzzy results as "suggestions", filters, ...
 */
export function useIndexSearch({ request, suggestions }) {
    const [searchState, setSearchState] = useState({
        keyword: null,
        filters: null,
        sortFields: null,
    });
    const debouncedSearchKeyword = useDebounce(searchState.keyword, 250);
    const [pages, setPages] = useState(0);
    const [page, setPage] = useState(0);
    const [error, setError] = useState(false);
    const [results, setResults] = useState([]);
    const [suggestionResults, setSuggestionResults] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setSearchLoading] = useState(false);
    const { fetchApi } = useFetchApi();
    const querySearchApi = useCallback(async ({ state, page, limit, fuzzy = false, length = 0, }) => {
        setSearchLoading(true);
        const response = await fetchApi(request, { body: { ...state, limit, fuzzy, page, filters: state.filters } }, { errorText: 'Fehler bei der Suche' });
        if (response === null || response.results === undefined) {
            return setError(true);
        }
        // check if we are loading more results
        if (page > 0 && length < response.total.all) {
            // append results to existing state when using page
            setResults((prev) => [...prev, ...response.results]);
        }
        else {
            setResults(response.results);
            // load suggestions if enabled
            if (suggestions?.enabled) {
                // if we have to many exact matches we reset suggestions to now show
                if (response.total.all > (suggestions?.maxResultCount ?? 5)) {
                    setSuggestionResults([]);
                }
                else {
                    const suggestionResponse = await fetchApi(request, {
                        body: {
                            ...state,
                            // remove id's that exits in exact query from fuzzy query
                            filters: {
                                searchFilterEntries: [
                                    ...state.filters.searchFilterEntries,
                                    ...filterExcludeResultIds(response.results),
                                ],
                                options: state.filters.options,
                            },
                            page: 0,
                            fuzzy: true,
                            limit: suggestions?.limit ?? 5,
                        },
                    }, { errorText: 'Fehler bei der Suche' });
                    if (suggestionResponse === null) {
                        return setError(true);
                    }
                    setSuggestionResults(suggestionResponse.results);
                }
            }
        }
        // load response into state
        setPage(page);
        setPages(response.pages);
        setTotal(response.total.all);
        setError(false);
        setSearchLoading(false);
    }, [
        fetchApi,
        suggestions?.enabled,
        suggestions?.maxResultCount,
        suggestions?.limit,
        request,
    ]);
    // request more results
    const loadMore = useCallback(async () => {
        if (debouncedSearchKeyword &&
            debouncedSearchKeyword.length >= 2 &&
            page < pages &&
            !loading) {
            await querySearchApi({
                state: {
                    keyword: searchState.keyword ?? '',
                    filters: {
                        searchFilterEntries: searchState.filters?.searchFilterEntries ?? [],
                        options: searchState.filters?.options,
                    },
                    sortFields: searchState.sortFields ?? {},
                },
                page: page + 1,
                length: results.length,
            });
        }
        // It doesn't need to run on every searchState change
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        debouncedSearchKeyword,
        loading,
        page,
        pages,
        querySearchApi,
        results.length,
    ]);
    // fetch on search state change
    useEffect(() => {
        // only fire when at least two chars got provided
        if (debouncedSearchKeyword && debouncedSearchKeyword.length >= 2) {
            // TODO: Should be handle null here or is it maybe better to pass null into the api request body?
            void querySearchApi({
                state: {
                    keyword: searchState.keyword ?? '',
                    filters: {
                        searchFilterEntries: searchState.filters?.searchFilterEntries ?? [],
                        options: searchState.filters?.options,
                    },
                    sortFields: searchState.sortFields ?? {},
                },
                page: 0,
            });
        }
        // It doesn't need to run on every searchState change
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchApi, debouncedSearchKeyword, setSearchLoading, querySearchApi]);
    return [
        {
            results,
            suggestions: suggestionResults,
            total,
            error,
            loading,
        },
        setSearchState,
        loadMore,
    ];
}
